// useSystemCollectionPurge.js

import { useCallback, useMemo, useState } from 'react';

import { toast } from 'react-toastify';

import useBaseAccess from 'hooks/useBaseAccess';
import { getPluralLabel, getSingularLabel } from 'utilities/Languages';
import { NODE_MODULE, normalizeNodeModule } from 'utilities/nodeModule';
import { isSystemTypeData } from 'security/typeAccess';
import { purgeNodeTypeBaseData } from 'services/nodetypes/NodeTypeMutationService';
import {
  beginBaseTypePurge,
  endBaseTypePurge,
} from 'services/databases/systemDatabasePurgeSession';

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Admin purge of system-collection nodes from base Settings → Collections.
 * Does not delete the system type definition — only base data for that type.
 *
 * @param {object} [options]
 * @param {string} [options.language='en']
 * @returns {object}
 */
export default function useSystemCollectionPurge({
  language = 'en',
} = {}) {
  const { can, isRoleLoading } = useBaseAccess();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [finalConfirmOpen, setFinalConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pending, setPending] = useState(null);

  const canPurgeSystemCollections = !isRoleLoading && can('types.delete');

  const canPurgeRow = useCallback((typeDef) => (
    canPurgeSystemCollections && isSystemTypeData(typeDef)
  ), [canPurgeSystemCollections]);

  const confirmMessage = useMemo(() => {
    if (!pending) return '';
    const typeKey = pending.typeKey;
    const typeDef = pending.typeDef;
    const typeTitle = getSingularLabel(typeDef, typeKey, language) || typeKey || 'this type';
    const typeTitlePlural = getPluralLabel(typeDef, typeKey, language)
      || (typeTitle === 'this type' ? 'records' : `${typeTitle}s`);
    return `Delete "${typeTitle}"? This will permanently delete ${typeTitlePlural} in this collection.`;
  }, [language, pending]);

  const openPurgeConfirm = useCallback(({
    baseKey,
    typeKey,
    typeDef = null,
    module = NODE_MODULE.PRIVATE,
  } = {}) => {
    const cleanBaseKey = normalizeString(baseKey);
    const cleanTypeKey = normalizeString(typeKey);
    if (!cleanBaseKey || !cleanTypeKey) return;
    if (!canPurgeSystemCollections || !isSystemTypeData(typeDef)) return;

    setPending({
      baseKey: cleanBaseKey,
      typeKey: cleanTypeKey,
      typeDef,
      module: normalizeNodeModule(module) || NODE_MODULE.PRIVATE,
    });
    setConfirmOpen(true);
  }, [canPurgeSystemCollections]);

  const handleFirstConfirm = useCallback(() => {
    setConfirmOpen(false);
    setFinalConfirmOpen(true);
  }, []);

  const handleFinalConfirm = useCallback(async () => {
    if (!pending || deleting) return;

    const { baseKey, typeKey, typeDef, module } = pending;
    if (!baseKey || !typeKey || !module) {
      toast.error('Collection is not ready to delete yet.');
      return;
    }

    setFinalConfirmOpen(false);
    setConfirmOpen(false);
    setDeleting(true);
    beginBaseTypePurge(baseKey);

    try {
      const result = await purgeNodeTypeBaseData({
        baseKey,
        typeKey,
        deleteNodes: true,
        module,
      });
      const deletedCount = Number(result?.deletedNodeCount || 0);
      const typeTitlePlural = getPluralLabel(typeDef, typeKey, language) || 'Records';
      toast.success(
        `${typeTitlePlural} removed. Deleted ${deletedCount} record${deletedCount === 1 ? '' : 's'}.`,
      );
      setPending(null);
    } catch (error) {
      toast.error(error?.message || 'Could not delete collection.');
    } finally {
      endBaseTypePurge(baseKey);
      setDeleting(false);
    }
  }, [deleting, language, pending]);

  return {
    canPurgeRow,
    canPurgeSystemCollections,
    confirmMessage,
    confirmOpen,
    deleting,
    finalConfirmOpen,
    handleFinalConfirm,
    handleFirstConfirm,
    openPurgeConfirm,
    setConfirmOpen,
    setFinalConfirmOpen,
  };
}
